import { Card, Modal, Row, Space, Typography } from 'antd';
import { ForwardedRef, forwardRef, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';
import { CustomizedDetailedUser } from '../../hooks/useUserById';
import { getUserById, mergeUser } from '../../services/people/userServices';
import { grispiNotification } from '../../store/useNotificationStore';
import {
    parseEmailFromUserFriendlyValue,
    parseFullNameFromUserFriendlyValue
} from '../../utils/forms/formHelper.parser';
import { getMaskedEmailForUI } from '../../utils/helpers';
import requestWithMessage from '../../utils/requestWithMessage';
import { getUsersPageUserRedirectUrlString, redirectTo } from '../../utils/routeHelper';
import { getTabStore } from '../../utils/store/getStore';
import { filterSWRKeys } from '../../utils/swr';
import AutoCompleteCustomerSearchInput from '../AutoCompleteCustomerSearchInput';
import { option } from '../CustomFieldComponent';
import ProfilePicture from '../ProfilePicture';

const styles = {
    card: {
        boxShadow: '0 1px 2px -2px rgb(0 0 0 / 16%), 0 3px 6px 0 rgb(0 0 0 / 12%), 0 5px 12px 4px rgb(0 0 0 / 9%)',
        width: 300
    },
    targetCard: {
        boxShadow: '0 1px 2px -2px rgb(0 0 0 / 16%), 0 3px 6px 0 rgb(0 0 0 / 12%), 0 5px 12px 4px rgb(0 0 0 / 9%)',
        width: 300,
        borderColor: '#632d91'
    }
};
interface UserMergeModalRef {
    open: () => void;
    close: () => void;
}

const UserMergeModal = forwardRef(
    ({ user }: { user: CustomizedDetailedUser }, ref: ForwardedRef<UserMergeModalRef>) => {
        const { t: tPeople } = useTranslation('people');
        const { mutate } = useSWRConfig();
        const [isUserMergeModalVisible, setIsUserMergeModalVisible] = useState(() => !user);
        const [targetUser, setTargetUser] = useState<option>(null);
        const [confirmLoading, setConfirmLoading] = useState(false);

        useImperativeHandle(
            ref,
            () => ({
                open() {
                    setIsUserMergeModalVisible(true);
                },
                close() {
                    setIsUserMergeModalVisible(false);
                }
            }),
            []
        );

        if (!user) return null;

        const handleOk = async () => {
            if (!targetUser) {
                grispiNotification('warning', tPeople('message.onNoTargetUsername'));
                return;
            }

            try {
                setConfirmLoading(true);

                const sourceId = user.id;
                const targetId = +targetUser.value;

                const targetUsername = parseEmailFromUserFriendlyValue(targetUser.label.toString());

                const startMessage = tPeople('startMessage.onMerge', {
                    sourceUsername: user?.primaryEmail?.email || sourceId,
                    targetUsername: targetUsername === '-' ? targetId : targetUsername || targetId
                });
                const finishMessage = tPeople('successMessage.onMerge', {
                    sourceUsername: user?.primaryEmail?.email || sourceId,
                    targetUsername: targetUsername === '-' ? targetId : targetUsername || targetId
                });
                await requestWithMessage({
                    key: 'merge-user',
                    startMessage,
                    finishMessage,
                    apiRequest: async () => {
                        await mergeUser({
                            sourceId,
                            targetId
                        });
                    }
                });

                setIsUserMergeModalVisible(false);
                setTargetUser(null);
                setConfirmLoading(false);

                const targetUserInfo = await getUserById(targetId);
                mutate(filterSWRKeys(`/users/${targetId}`), targetUserInfo);
                getTabStore().closeTabAndGetNewTargetUrl(`USER-${user.id}`);
                redirectTo(getUsersPageUserRedirectUrlString(targetUserInfo?.id, targetUserInfo?.role?.authority)); // re-direct to target customer
            } catch (error) {
                setConfirmLoading(false);
            }
        };

        const onCancel = () => {
            setTargetUser(null);
            setIsUserMergeModalVisible(false);
        };

        return (
            <>
                <Modal
                    confirmLoading={confirmLoading}
                    destroyOnClose
                    onCancel={onCancel}
                    onOk={handleOk}
                    open={isUserMergeModalVisible}
                    title={tPeople('userMerge')}
                >
                    <Space direction="vertical" className="full-width" size="large">
                        <Row justify="center">
                            <Card style={styles.card}>
                                <Card.Meta
                                    avatar={<ProfilePicture size="large" src={user?.profilePicture} />}
                                    title={user?.fullName ?? getMaskedEmailForUI(user?.primaryEmail?.email)}
                                    description={user?.primaryEmail?.email}
                                />
                            </Card>
                        </Row>
                        <AutoCompleteCustomerSearchInput
                            removeIds={[user?.id]}
                            value={targetUser}
                            onChange={(_option, newTargetUser: option) => setTargetUser(newTargetUser)}
                            onClear={() => setTargetUser(null)}
                            allowClear
                        />
                        {targetUser && (
                            <Row justify="center">
                                <Typography.Paragraph>
                                    <Typography.Text strong>
                                        {tPeople('infoText.onMerge.title', {
                                            sourceUsername:
                                                user?.fullName ?? getMaskedEmailForUI(user?.primaryEmail?.email),
                                            targetUsername: parseFullNameFromUserFriendlyValue(
                                                targetUser?.label?.toString()
                                            )
                                        })}
                                    </Typography.Text>
                                    <ul>
                                        <li>
                                            <Typography.Text>
                                                {tPeople('infoText.onMerge.item1', {
                                                    sourceUsername:
                                                        user?.fullName ?? getMaskedEmailForUI(user?.primaryEmail?.email)
                                                })}
                                            </Typography.Text>
                                        </li>
                                        <li>
                                            {tPeople('infoText.onMerge.item2', {
                                                sourceUsername:
                                                    user?.fullName ?? getMaskedEmailForUI(user?.primaryEmail?.email)
                                            })}
                                        </li>
                                    </ul>
                                </Typography.Paragraph>
                                <Card style={styles.targetCard}>
                                    <Card.Meta
                                        avatar={<ProfilePicture size="large" src={targetUser?.profilePicture} />}
                                        title={targetUser?.label?.toString()?.split('(')?.[0]}
                                        description={parseEmailFromUserFriendlyValue(targetUser?.label?.toString())}
                                    />
                                </Card>
                            </Row>
                        )}
                    </Space>
                </Modal>
            </>
        );
    }
);

export default UserMergeModal;
export { type UserMergeModalRef };
